import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Percent, TrendingUp, TrendingDown, Minus, Ruler } from "lucide-react";

type FoilPricing = {
  id: string;
  foil_key: string;
  foil_label: string;
  adjustment_percent: number;
};

// Price calculation logic (same as Kalkulator.tsx)
const PRICE_TABLES: Record<number, { areas: number[]; prices: number[] }> = {
  1:    { areas: [0.25, 0.5, 1, 2, 4, 15, 50, 100, 150, 300, 500, 800, 1100], prices: [30.54, 37.83, 46.78, 54.89, 62.37, 62.37, 62.37, 62.37, 62.37, 62.37, 62.37, 62.37, 62.37] },
  50:   { areas: [0.25, 0.5, 1, 2, 4, 15, 50, 100, 150, 300, 500, 800, 1100], prices: [1.338, 1.681, 2.0493, 2.4045, 2.7324, 2.7324, 4.5144, 6.8112, 9.2862, 15.50, 23.00, 34.00, 45.00] },
  100:  { areas: [0.25, 0.5, 1, 2, 4, 15, 50, 100, 150, 300, 500, 800, 1100], prices: [0.771, 0.968, 1.1806, 1.3852, 1.5741, 1.8612, 3.4056, 5.5737, 7.7319, 13.00, 19.50, 29.00, 38.50] },
  200:  { areas: [0.25, 0.5, 1, 2, 4, 15, 50, 100, 150, 300, 500, 800, 1100], prices: [0.431, 0.542, 0.6608, 0.7754, 0.8811, 1.2276, 2.7819, 4.7421, 6.435, 10.80, 16.20, 24.50, 32.50] },
  300:  { areas: [0.25, 0.5, 1, 2, 4, 15, 50, 100, 150, 300, 500, 800, 1100], prices: [0.334, 0.420, 0.5123, 0.6011, 0.6831, 1.0692, 2.574, 4.2867, 6.2469, 10.00, 15.00, 22.80, 30.20] },
  500:  { areas: [0.25, 0.5, 1, 2, 4, 15, 50, 100, 150, 300, 500, 800, 1100], prices: [0.228, 0.286, 0.3490, 0.4095, 0.4653, 0.9306, 2.1681, 4.1283, 5.8806, 9.50, 14.20, 21.50, 28.50] },
};
const QTY_LIST = [1, 50, 100, 200, 300, 500];

function lerpVal(x: number, xs: number[], ys: number[]) {
  if (x <= xs[0]) return ys[0];
  if (x >= xs[xs.length - 1]) return ys[ys.length - 1];
  for (let i = 0; i < xs.length - 1; i++) {
    if (xs[i] <= x && x <= xs[i + 1]) {
      const t = (x - xs[i]) / (xs[i + 1] - xs[i]);
      return ys[i] + t * (ys[i + 1] - ys[i]);
    }
  }
  return ys[ys.length - 1];
}

function calcPreviewPrice(wMm: number, hMm: number, qty: number, globalAdjust: number, foilAdjust: number, qtyAdjust: number = 0) {
  if (!wMm || !hMm || wMm < 5 || hMm < 5 || qty < 1) return null;
  const areaCm2 = (wMm / 10) * (hMm / 10);
  function unitForQty(q: number) {
    const tbl = PRICE_TABLES[q];
    return lerpVal(areaCm2, tbl.areas, tbl.prices);
  }
  let unitNet: number;
  if (qty <= 1) {
    unitNet = unitForQty(1);
  } else if (qty < 50) {
    const base50 = unitForQty(50);
    const sqBreaks = [2, 5, 10, 15, 20, 25, 35, 49];
    const sqSurcharges = [220, 164, 76, 45, 32, 21, 17.6, 0];
    const surcharge = lerpVal(qty, sqBreaks, sqSurcharges);
    unitNet = base50 * (1 + surcharge / 100);
  } else {
    const qtyLower = QTY_LIST.filter((q) => q <= qty).pop() || QTY_LIST[0];
    const qtyUpper = QTY_LIST.find((q) => q > qty) || QTY_LIST[QTY_LIST.length - 1];
    if (qtyLower === qtyUpper) {
      unitNet = unitForQty(qtyLower);
    } else {
      const t = (qty - qtyLower) / (qtyUpper - qtyLower);
      unitNet = unitForQty(qtyLower) + t * (unitForQty(qtyUpper) - unitForQty(qtyLower));
    }
  }
  unitNet = unitNet * (1 + globalAdjust / 100) * (1 + foilAdjust / 100) * (1 + qtyAdjust / 100);
  unitNet = Math.round(unitNet * 100) / 100;
  return unitNet;
}

const FoilPricingSettings = () => {
  const [foils, setFoils] = useState<FoilPricing[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<string | null>(null);
  const [editValues, setEditValues] = useState<Record<string, string>>({});
  const [globalAdjust, setGlobalAdjust] = useState(0);
  const [previewW, setPreviewW] = useState("50");
  const [previewH, setPreviewH] = useState("20");
  const [previewQty, setPreviewQty] = useState("100");

  useEffect(() => {
    fetchFoils();
    supabase
      .from("pricing_settings")
      .select("price_adjustment_percent")
      .limit(1)
      .single()
      .then(({ data }) => {
        if (data) setGlobalAdjust(Number(data.price_adjustment_percent));
      });
  }, []);

  const fetchFoils = async () => {
    const { data } = await supabase
      .from("foil_pricing")
      .select("*")
      .order("foil_label", { ascending: true });
    if (data) {
      setFoils(data as FoilPricing[]);
      const vals: Record<string, string> = {};
      data.forEach((f: any) => { vals[f.id] = String(f.adjustment_percent); });
      setEditValues(vals);
    }
    setLoading(false);
  };

  const [savedIds, setSavedIds] = useState<Record<string, boolean>>({});

  const handleSave = async (foil: FoilPricing) => {
    setSaving(foil.id);
    const newVal = parseFloat(editValues[foil.id]) || 0;
    const { error } = await supabase
      .from("foil_pricing")
      .update({ adjustment_percent: newVal, updated_at: new Date().toISOString() })
      .eq("id", foil.id);
    if (error) {
      toast.error("Błąd zapisu");
    } else {
      toast.success(`Zapisano: ${foil.foil_label}`);
      setFoils((prev) => prev.map((f) => f.id === foil.id ? { ...f, adjustment_percent: newVal } : f));
      setSavedIds((prev) => ({ ...prev, [foil.id]: true }));
      setTimeout(() => setSavedIds((prev) => ({ ...prev, [foil.id]: false })), 2000);
    }
    setSaving(null);
  };

  const handleSaveAll = async () => {
    setSaving("all");
    const updates = foils.map((f) => {
      const newVal = parseFloat(editValues[f.id]) || 0;
      return supabase
        .from("foil_pricing")
        .update({ adjustment_percent: newVal, updated_at: new Date().toISOString() })
        .eq("id", f.id);
    });
    await Promise.all(updates);
    await fetchFoils();
    toast.success("Wszystkie korekty zapisane");
    setSaving(null);
  };

  const wMm = parseInt(previewW) || 50;
  const hMm = parseInt(previewH) || 20;
  const qty = parseInt(previewQty) || 100;

  if (loading) return <p className="text-muted-foreground text-sm">Ładowanie ustawień folii...</p>;

  return (
    <div className="bg-card border border-border rounded-xl p-6 space-y-5">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Percent className="w-5 h-5 text-accent" />
          <h2 className="text-lg font-bold text-foreground">Korekta cen folii</h2>
        </div>
        <Button onClick={handleSaveAll} disabled={saving === "all"} size="sm">
          {saving === "all" ? "Zapisywanie..." : "Zapisz wszystko"}
        </Button>
      </div>

      <p className="text-muted-foreground text-sm">
        Ustaw procentową korektę cen dla każdej folii osobno. Wartość dodatnia (+) podwyższa cenę, ujemna (-) obniża.
      </p>

      {/* Preview size settings */}
      <div className="flex flex-wrap items-center gap-3 bg-muted/50 rounded-lg p-3 border border-border">
        <Ruler className="w-4 h-4 text-muted-foreground shrink-0" />
        <span className="text-xs font-medium text-muted-foreground">Podgląd ceny dla naklejki:</span>
        <div className="flex items-center gap-1.5">
          <Input
            type="number"
            value={previewW}
            onChange={(e) => setPreviewW(e.target.value)}
            className="w-16 h-7 text-center text-xs"
            min={5}
            max={380}
          />
          <span className="text-xs text-muted-foreground">×</span>
          <Input
            type="number"
            value={previewH}
            onChange={(e) => setPreviewH(e.target.value)}
            className="w-16 h-7 text-center text-xs"
            min={5}
            max={280}
          />
          <span className="text-xs text-muted-foreground">mm</span>
        </div>
        <div className="flex items-center gap-1.5">
          <Input
            type="number"
            value={previewQty}
            onChange={(e) => setPreviewQty(e.target.value)}
            className="w-20 h-7 text-center text-xs"
            min={1}
            max={500}
          />
          <span className="text-xs text-muted-foreground">szt.</span>
        </div>
      </div>

      <div className="space-y-2">
        {foils.map((foil) => {
          const val = parseFloat(editValues[foil.id]) || 0;
          const previewPrice = calcPreviewPrice(wMm, hMm, qty, globalAdjust, val);
          const basePriceNoFoil = calcPreviewPrice(wMm, hMm, qty, globalAdjust, 0);
          return (
            <div key={foil.id} className="flex items-center gap-3 bg-background rounded-lg p-3 border border-border">
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-foreground truncate">{foil.foil_label}</p>
                {previewPrice !== null && (
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {qty} szt. → <span className="font-bold text-foreground">{(previewPrice * qty).toFixed(2)} zł</span>
                    <span className="text-muted-foreground/60 ml-1">({previewPrice.toFixed(2)} zł/szt. netto)</span>
                  </p>
                )}
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <button
                  onClick={() => setEditValues((prev) => ({ ...prev, [foil.id]: String(Math.round(((parseFloat(prev[foil.id]) || 0) - 0.5) * 10) / 10) }))}
                  className="w-7 h-7 rounded bg-muted flex items-center justify-center text-muted-foreground hover:text-foreground transition-colors"
                >
                  <Minus className="w-3 h-3" />
                </button>
                <Input
                  type="number"
                  step="0.5"
                  value={editValues[foil.id] ?? "0"}
                  onChange={(e) => setEditValues((prev) => ({ ...prev, [foil.id]: e.target.value }))}
                  className="w-20 h-8 text-center text-sm"
                />
                <button
                  onClick={() => setEditValues((prev) => ({ ...prev, [foil.id]: String(Math.round(((parseFloat(prev[foil.id]) || 0) + 0.5) * 10) / 10) }))}
                  className="w-7 h-7 rounded bg-muted flex items-center justify-center text-muted-foreground hover:text-foreground transition-colors"
                >
                  <TrendingUp className="w-3 h-3" />
                </button>
                <span className="w-14 text-right text-xs font-bold flex items-center justify-end gap-0.5">
                  {val > 0 && <TrendingUp className="w-3 h-3 text-green-600" />}
                  {val < 0 && <TrendingDown className="w-3 h-3 text-red-600" />}
                  <span className={val > 0 ? "text-green-600" : val < 0 ? "text-red-600" : "text-muted-foreground"}>
                    {val > 0 ? "+" : ""}{val}%
                  </span>
                </span>
                <Button
                  size="sm"
                  variant="outline"
                  className="h-8 text-xs"
                  onClick={() => handleSave(foil)}
                  disabled={saving === foil.id}
                >
                  {saving === foil.id ? "..." : savedIds[foil.id] ? "✓ Zapisano" : "Zapisz"}
                </Button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default FoilPricingSettings;
